/**
 * Deployment staleness + local sign-in state reset.
 *
 * wisp serves every file (including index.html) with `cache-control:
 * max-age=600`, so a browser can keep running a bundle that the server has
 * already replaced. Nothing in the old bundle knows it is old, which makes a
 * stale build look exactly like a broken app. version.json (written by
 * scripts/postbuild-version.mjs) closes that gap.
 */
import { assetUrl } from './paths.js';

const SUB_KEY = '@@atproto/oauth-client-browser(sub)';
const DB_NAME = '@atproto-oauth-client';

function loadedBundleName() {
  const script = document.querySelector('script[src]');

  if (!script) return null;

  return script.src.split('/').pop() || null;
}

/**
 * @returns {Promise<{stale: boolean, running: string|null, deployed: string|null, builtAt?: string}>}
 */
export async function checkDeployment() {
  const running = loadedBundleName();

  try {
    const res = await fetch(assetUrl('version.json'), { cache: 'no-store' });

    if (!res.ok) return { stale: false, running, deployed: null };
    const info = await res.json();

    return {
      stale: Boolean(running && info.bundle && running !== info.bundle),
      running,
      deployed: info.bundle || null,
      builtAt: info.builtAt,
    };
  } catch {
    return { stale: false, running, deployed: null };
  }
}

/** Clear everything this app stores locally for OAuth, then reload. */
export async function resetLocalSignInState() {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.includes('atproto') || key === SUB_KEY) localStorage.removeItem(key);
    }
  } catch {
    /* storage blocked: nothing to clear */
  }

  await new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = resolve;
      req.onerror = resolve;
      req.onblocked = resolve;
    } catch {
      resolve();
    }
  });

  window.location.replace(window.location.pathname);
}
